import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type {
  ClickableAnyRewardConfig_anyRewardConfigInterface,
  ClickableAnyRewardConfig_so5League,
} from './__generated__/index.graphql';

const DropdownLabel = styled.div`
  width: 100%;
  height: 100%;
`;
const DropdownContent = styled.div`
  padding: var(--intermediate-unit) var(--double-unit);
`;

const ClickableAnyRewardConfigWrapper = ({
  children,
  anyRewardConfig,
  so5League,
}: {
  children: ReactNode;
  anyRewardConfig: ClickableAnyRewardConfig_anyRewardConfigInterface;
  so5League?: ClickableAnyRewardConfig_so5League;
}): ReactNode => {
  const { sport } = useSportContext();

  let title: string | undefined;
  let description: string | undefined;
  if (isType(anyRewardConfig, 'DeliverableItemRewardConfig')) {
    title = anyRewardConfig.deliverableItem.name;
  } else if (isType(anyRewardConfig, 'ExperienceRewardConfig')) {
    title = anyRewardConfig.title || undefined;
    description = anyRewardConfig.description || undefined;
  } else if (isType(anyRewardConfig, 'ShopItemRewardConfig')) {
    description = anyRewardConfig.shopItem.description || undefined;
  }

  if (title || description) {
    return (
      <Dropdown
        triggerOnHover
        gap={8}
        label={props => (
          <LinkOther as={ButtonBase} {...props}>
            <DropdownLabel>{children}</DropdownLabel>
          </LinkOther>
        )}
      >
        <DropdownContent>
          <Vertical gap={0}>
            {[title, description].filter(Boolean).map((str, i) => {
              const Wrapper = i === 0 ? BodyS : LabelS;

              return <Wrapper key={str}>{str}</Wrapper>;
            })}
          </Vertical>
        </DropdownContent>
      </Dropdown>
    );
  }

  if (
    isType(anyRewardConfig, 'CardRewardConfig') &&
    so5League &&
    anyRewardConfig.quality
  ) {
    return (
      <LinkOther
        as={Link}
        to={generateSportPath(ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY, {
          params: {
            rarity: anyRewardConfig.configRarity,
            quality: anyRewardConfig.quality,
            league: so5League.slug,
            fixture: so5League.so5Fixture.slug,
          },
          sport,
        })}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </LinkOther>
    );
  }

  return children;
};

type ClickableAnyRewardConfigProps = {
  anyRewardConfig: ClickableAnyRewardConfig_anyRewardConfigInterface;
  so5League?: ClickableAnyRewardConfig_so5League;
} & Pick<
  ComponentProps<typeof AnyRewardConfig>,
  'rarity' | 'variant' | 'hideText' | 'customText' | 'textVariantSize'
>;

export const ClickableAnyRewardConfig = ({
  anyRewardConfig,
  so5League,
  ...anyRewardConfigProps
}: ClickableAnyRewardConfigProps) => {
  return (
    <LinkBox>
      <ClickableAnyRewardConfigWrapper
        anyRewardConfig={anyRewardConfig}
        so5League={so5League}
      >
        <AnyRewardConfig
          anyRewardConfig={anyRewardConfig}
          {...anyRewardConfigProps}
        />
      </ClickableAnyRewardConfigWrapper>
    </LinkBox>
  );
};

ClickableAnyRewardConfig.fragments = {
  anyRewardConfigInterface: gql`
    fragment ClickableAnyRewardConfig_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on CardRewardConfig {
        id
        quality: qualityTyped
        configRarity
      }

      ... on DeliverableItemRewardConfig {
        id
        deliverableItem {
          slug
          name
        }
      }
      ... on ExperienceRewardConfig {
        id
        title
        description
      }
      ... on ShopItemRewardConfig {
        id
        shopItem {
          id
          description
        }
      }
      ...AnyRewardConfig_anyRewardConfigInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ClickableAnyRewardConfig_anyRewardConfigInterface>,
  so5League: gql`
    fragment ClickableAnyRewardConfig_so5League on So5League {
      slug
      so5Fixture {
        slug
      }
    }
  ` as TypedDocumentNode<ClickableAnyRewardConfig_so5League>,
};
